import {
  AccountCertification,
  CertificationType,
} from '@wix/ambassador-academy-certifications-v1-account-certification/types';
import _ from 'lodash';
import { useMemo } from 'react';

interface UseGroupedAccountCertificationsParams {
  accountCertifications?: AccountCertification[];
}

const sortCertificationsChronological = (
  cert1: AccountCertification,
  cert2: AccountCertification,
) => {
  return (
    new Date(cert2.createdDate!).getTime() -
    new Date(cert1.createdDate!).getTime()
  );
};

export const useGroupedAccountCertifications = ({
  accountCertifications,
}: UseGroupedAccountCertificationsParams) => {
  const groupedCertificationResults = useMemo(() => {
    if (!accountCertifications || _.isEmpty(accountCertifications)) {
      return {};
    }

    accountCertifications.sort(sortCertificationsChronological);

    const groupedCertifications = accountCertifications.reduce(
      (accum, current) => {
        if (!current?.certificationType) {
          return accum;
        }

        const certificationGroup = (
          CertificationTypeToGroupMap as CertificationGroupMapType
        )?.[current.certificationType];

        const certificationType = (certificationGroup ??
          current.certificationType) as CertificationGroupType;

        const entryForType = accum[certificationType];
        if (Array.isArray(entryForType)) {
          accum[certificationType].push(current);
        } else {
          accum[certificationType] = [current];
        }
        return accum;
      },
      {} as Record<CertificationGroupType, AccountCertification[]>,
    );

    const mostRecentOfEachGroup = Object.entries(groupedCertifications).map(
      ([key, certsForGroup]) => {
        const mostRecentForGroup = certsForGroup[0];
        const entry = {
          groupType: key as CertificationGroupType,
          createdDate: mostRecentForGroup?.createdDate!,
        };
        return entry;
      },
    );

    const sortedCertificationTypes = mostRecentOfEachGroup.sort(
      sortCertificationsChronological,
    );

    const certificationGroups = Object.keys(
      groupedCertifications,
    ) as CertificationGroupType[];

    return {
      sortedCertificationTypes,
      groupedCertifications,
      certificationGroups,
    };
  }, [accountCertifications]);

  return {
    ...groupedCertificationResults,
  };
};

const CertificationGroups = ['DEVELOPER', 'DESIGNER'] as const;
type CertificationGroup = (typeof CertificationGroups)[number];

const CertificationsWithGroups = [
  CertificationType.STUDIO_DEVELOPER,
  CertificationType.STUDIO_DEV_WEBSITES_LEAGUE,
  CertificationType.STUDIO_DESIGN_LEAGUE,
  CertificationType.WEB_DESIGNER,
] as const;
type ICertificationsWithGroups = (typeof CertificationsWithGroups)[number];

const CertificationTypeToGroupMap: Record<
  ICertificationsWithGroups,
  CertificationGroup
> = {
  [CertificationType.STUDIO_DEVELOPER]: 'DEVELOPER',
  [CertificationType.STUDIO_DEV_WEBSITES_LEAGUE]: 'DEVELOPER',
  [CertificationType.STUDIO_DESIGN_LEAGUE]: 'DESIGNER',
  [CertificationType.WEB_DESIGNER]: 'DESIGNER',
} as const;

export type CertificationGroupType = Exclude<
  `${CertificationType}` | CertificationGroup,
  `${ICertificationsWithGroups}`
>;

type CertificationGroupMapType = typeof CertificationTypeToGroupMap & {
  [key: string]: undefined;
};
